import React, { useState, useRef, useEffect } from "react";
import { Search, Camera, Book, X, Check, RefreshCw, Clock } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import "../../css/Books.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../components/Loader";
function Books() {
  const [activeTab, setActiveTab] = useState("automatic");
  const [activeAction, setActiveAction] = useState("issue"); // "issue" or "return"
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [returnedBooks, setReturnedBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastProcessed, setLastProcessed] = useState(null);
  const html5QrcodeScannerRef = useRef(null);
  const isProcessingRef = useRef(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalBookTitle, setModalBookTitle] = useState("");
  const [currentStudent, setCurrentStudent] = useState(null);

  useEffect(() => {
    if (activeTab !== "automatic" && isScannerActive) {
      stopScanner();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "manual") {
      fetchStudents();
    }
  }, [activeTab]);

  useEffect(() => {
    return () => {
      if (html5QrcodeScannerRef.current) {
        stopScanner();
      }
    };
  }, []);

  const fetchStudents = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please login again.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch students");
      }

      const data = await response.json();
      setStudents(data);
      if (data.length === 0) {
        toast.info("No students found");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error(error.message);
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const startScanner = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Your browser doesn't support camera access");
      }
      setIsScannerActive(true);

      setTimeout(async () => {
        try {
          const qrReaderContainer = document.getElementById("books-qr-reader-container");
          if (!qrReaderContainer) {
            throw new Error("Scanner container not found in DOM");
          }

          if (html5QrcodeScannerRef.current) {
            try {
              await html5QrcodeScannerRef.current.stop();
              html5QrcodeScannerRef.current = null;
            } catch (stopError) {
              console.warn("Error stopping previous scanner:", stopError);
              html5QrcodeScannerRef.current = null;
            }
          }

          toast.info("Starting camera, please allow camera access...");
          const html5QrCode = new Html5Qrcode("books-qr-reader-container");
          html5QrcodeScannerRef.current = html5QrCode;

          const qrCodeSuccessCallback = async (decodedText) => {
            if (isProcessingRef.current) {
              return;
            }

            try {
              isProcessingRef.current = true;
              await handleStudentQRScan(decodedText);
              setTimeout(() => {
                isProcessingRef.current = false;
              }, 1000);
            } catch (err) {
              console.error("Error in QR code processing:", err);
              toast.error(`Error: ${err.message || "QR code processing failed"}`);
              isProcessingRef.current = false;
            }
          };

          const qrCodeErrorCallback = (error) => {
            if (error && error.message && !error.message.includes("No QR code found")) {
              console.warn("QR scanner error:", error);
            }
          };

          const config = {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            disableFlip: false,
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true
            }
          };

          await html5QrCode.start(
            { facingMode: "environment" },
            config,
            qrCodeSuccessCallback,
            qrCodeErrorCallback
          );

          toast.success("QR code scanner started successfully");
        } catch (error) {
          console.error("Error starting scanner:", error);
          toast.error(`Scanner error: ${error.message || "Failed to initialize scanner"}`);
          setIsScannerActive(false);
          if (html5QrcodeScannerRef.current) {
            try {
              await html5QrcodeScannerRef.current.stop();
              html5QrcodeScannerRef.current = null;
            } catch (e) {
              console.warn("Error cleaning up scanner:", e);
              html5QrcodeScannerRef.current = null;
            }
          }
        }
      }, 500);
    } catch (error) {
      console.error("Scanner initialization error:", error);
      toast.error(`Failed to initialize scanner: ${error.message || "Unknown error"}`);
      setIsScannerActive(false);
    }
  };

  const stopScanner = async () => {
    if (!html5QrcodeScannerRef.current) {
      setIsScannerActive(false);
      return;
    }

    try {
      isProcessingRef.current = true;
      await html5QrcodeScannerRef.current.stop();
      toast.info("Scanner stopped");
    } catch (error) {
      console.error("Error stopping scanner:", error);
    } finally {
      html5QrcodeScannerRef.current = null;
      isProcessingRef.current = false;
      setIsScannerActive(false);
    }
  };

  // Modified to identify students only from QR code
  const handleStudentQRScan = async (qrCodeValue) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in again.");
      return;
    }

    toast.info(`Identifying student...`);
    try {
      // Get student info from QR code
      const endpoint = activeAction === "issue"
        ? "https://schoolpro-api.uniums.com/api/books/issue/scan"
        : "https://schoolpro-api.uniums.com/api/books/return/scan";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ barcodeValue: qrCodeValue }),
      });

      const data = await response.json();

      if (response.ok && data.student) {
        // Student identified successfully, pause scanner temporarily
        const isPaused = html5QrcodeScannerRef.current ? await html5QrcodeScannerRef.current.pause() : true;

        if (isPaused) {
          // Store student info
          setCurrentStudent(data.student);
          // Open modal to enter book details
          setModalBookTitle("");
          setIsModalOpen(true);
          toast.success(`Student identified: ${data.student.firstName} ${data.student.lastName}`);
        } else {
          toast.error("Could not pause scanner to process book information");
        }
      } else {
        toast.error(data.message || "Could not identify student from QR code");
      }
    } catch (error) {
      console.error("QR code processing error:", error);
      toast.error(`Failed to process QR code: ${error.message}`);
    }
  };

  // Open book modal for manual selection
  const openBookModal = (student) => {
    setCurrentStudent(student);
    setModalBookTitle("");
    setIsModalOpen(true);
  };

  const closeBookModal = () => {
    setIsModalOpen(false);
    setModalBookTitle("");

    // Resume scanner if it was active
    if (isScannerActive && html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.resume().catch(error => {
        console.error("Error resuming scanner:", error);
      });
    }

    setCurrentStudent(null);
  };

  const handleModalSubmit = async () => {
    if (!modalBookTitle.trim()) {
      toast.error("Please enter a book title");
      return;
    }

    if (!currentStudent) {
      toast.error("No student selected");
      closeBookModal();
      return;
    }

    const token = localStorage.getItem("token");
    try {
      // Different endpoints for scan vs manual and issue vs return
      let endpoint;
      if (activeTab === "automatic") {
        endpoint = activeAction === "issue"
          ? "https://schoolpro-api.uniums.com/api/books/complete-issue"
          : "https://schoolpro-api.uniums.com/api/books/complete-return";
      } else {
        endpoint = activeAction === "issue"
          ? "https://schoolpro-api.uniums.com/api/books/issue/manual"
          : "https://schoolpro-api.uniums.com/api/books/return/manual";
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          studentId: currentStudent._id,
          bookTitle: modalBookTitle
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const recordWithTimestamp = {
          ...currentStudent,
          bookTitle: modalBookTitle,
          processedTime: new Date().toLocaleTimeString(),
          bookId: data.bookId || "N/A",
        };

        if (activeAction === "issue") {
          setIssuedBooks((prev) => [recordWithTimestamp, ...prev]);
          toast.success(`Book "${modalBookTitle}" issued to ${currentStudent.firstName} ${currentStudent.lastName}`);
        } else {
          setReturnedBooks((prev) => [recordWithTimestamp, ...prev]);
          toast.success(`Book "${modalBookTitle}" returned by ${currentStudent.firstName} ${currentStudent.lastName}`);
        }

        setLastProcessed(currentStudent);
        closeBookModal();

        // Resume scanner if it was active
        if (isScannerActive && html5QrcodeScannerRef.current) {
          html5QrcodeScannerRef.current.resume().catch(error => {
            console.error("Error resuming scanner:", error);
          });
        }
      } else {
        throw new Error(data.message || `Failed to ${activeAction} book`);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="books-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="books-header">
        <h2>Manage Books</h2>
        <div className="books-tabs">
          <button
            className={`books-tab ${activeTab === "automatic" ? "active" : ""}`}
            onClick={() => setActiveTab("automatic")}
          >
            Automatic (QR Code)
          </button>
          <button
            className={`books-tab ${activeTab === "manual" ? "active" : ""}`}
            onClick={() => setActiveTab("manual")}
          >
            Manual
          </button>
        </div>
      </div>

      <div className="books-action-toggle">
        <button
          className={`books-action-btn ${activeAction === "issue" ? "active" : ""}`}
          onClick={() => setActiveAction("issue")}
        >
          Issue Books
        </button>
        <button
          className={`books-action-btn ${activeAction === "return" ? "active" : ""}`}
          onClick={() => setActiveAction("return")}
        >
          Return Books
        </button>
      </div>

      {activeTab === "automatic" ? (
        <div className="books-scanner-container">
          <div className="books-scanner">
            {!isScannerActive ? (
              <button className="books-start-btn" onClick={startScanner}>
                <Camera size={24} />
                Start QR Scanner
              </button>
            ) : (
              <>
                <div
                  id="books-qr-reader-container"
                  className="books-viewport"
                  style={{
                    width: "100%",
                    height: "300px",
                    position: "relative",
                    border: "2px solid #3498db",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                />
                <button className="books-stop-btn" onClick={stopScanner}>
                  <X size={20} />
                  Stop Scanner
                </button>
                <div className="scanner-instructions">
                  <p>Scan student ID card to {activeAction === "issue" ? "issue" : "return"} a book</p>
                  {lastProcessed && (
                    <div className="books-last-processed">
                      Last processed: {lastProcessed.firstName} {lastProcessed.lastName}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="books-processed-list">
            <h3>{activeAction === "issue" ? "Issued Books" : "Returned Books"}</h3>
            {(activeAction === "issue" ? issuedBooks : returnedBooks).length === 0 ? (
              <div className="books-no-records">
                No books {activeAction === "issue" ? "issued" : "returned"} yet
              </div>
            ) : (
              (activeAction === "issue" ? issuedBooks : returnedBooks).map((record, index) => (
                <div key={`${record._id}-${index}`} className="books-processed-item">
                  <div className="books-processed-info">
                    <span className="books-student-name">
                      {record.firstName} {record.lastName}
                    </span>
                    <span className="books-title">{record.bookTitle}</span>
                  </div>
                  <span className="books-time">
                    {activeAction === "issue" ? (
                      <Check size={14} />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    {record.processedTime}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="books-manual">
          <div className="books-search">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {isLoading ? (
            <div className="books-loading">Loading students...</div>
          ) : (
            <div className="books-students-list">
              {students
                .filter((student) =>
                  `${student.firstName} ${student.lastName}`
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  (student.rollNumber && student.rollNumber
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()))
                )
                .map((student) => (
                  <div key={student._id} className="books-student-item">
                    <div className="books-student-info">
                      <span className="books-name">
                        {student.firstName} {student.lastName}
                      </span>
                      <span className="books-id">{student.rollNumber}</span>
                    </div>
                    <button
                      className={`books-action-button-styled ${activeAction === "issue" ? "books-issue-btn" : "books-return-btn"}`}
                      onClick={() => openBookModal(student)}
                    >
                      {activeAction === "issue" ? (
                        <>
                          <Book size={20} />
                          Issue Book
                        </>
                      ) : (
                        <>
                          <RefreshCw size={20} />
                          Return Book
                        </>
                      )}
                    </button>
                  </div>
                ))}
              {students.length === 0 && !isLoading && (
                <div className="books-no-students">
                  No students found. Please add students first.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Book Details Modal */}
      {isModalOpen && (
        <div className="books-modal-overlay">
          <div className="books-modal">
            <div className="books-modal-header">
              <h3>
                {activeAction === "issue" ? "Issue Book to" : "Return Book from"}{" "}
                {currentStudent && `${currentStudent.firstName} ${currentStudent.lastName}`}
              </h3>
              <button className="books-modal-close" onClick={closeBookModal}>
                <X size={20} />
              </button>
            </div>
            <div className="books-modal-body">
              <label className="books-modal-label">
                Enter book title:
                <input
                  type="text"
                  className="books-modal-input"
                  value={modalBookTitle}
                  onChange={(e) => setModalBookTitle(e.target.value)}
                  placeholder="Book title..."
                  autoFocus
                />
              </label>
            </div>
            <div className="books-modal-footer">
              <button className="books-modal-cancel" onClick={closeBookModal}>
                Cancel
              </button>
              <button
                className={`books-modal-submit ${activeAction === "issue" ? "books-issue-submit" : "books-return-submit"}`}
                onClick={handleModalSubmit}
              >
                {activeAction === "issue" ? "Issue Book" : "Return Book"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Books;